/**
 * `/audit` (P1, docs/KONZEPT-ADMIN-BEREICH.md § "Informationsarchitektur & UI", Tab-7-Muster:
 * "Tabelle: Zeit, Admin, Aktion, Kurzfassung; Zeilen-Klick öffnet Drawer mit dem vollständigen
 * Payload als `<pre>`-JSON"). Plattformweite Sicht auf `platform_admin_audit_log`, ausschließlich
 * über `GET /api/admin/audit-log` (Migration 372, siehe `apps/api/src/routes/admin.ts`
 * `auditLogQuerySchema`/`AuditLogRow` — Filter-/Cursor-/Antwortformat von dort 1:1 übernommen,
 * nicht geraten).
 *
 * Filter (Umfang bewusst minimal gehalten, Auftrag erlaubt das ausdrücklich — "mind.
 * Aktions-Filter, optional Zeitraum/Mandant"): `action` (Text) + `von`/`bis` (Datum). KEIN
 * eigenes Mandanten-Auswahl-Dropdown in dieser Iteration — es bräuchte einen zusätzlichen
 * Netzwerk-Ruf (`GET /api/admin/tenants`) nur dafür, das Konzept nennt ihn als "optional".
 * `tenantId` als PROP (statt Dropdown) kommt seit der zweiten P1-Iteration
 * (docs/KONZEPT-ADMIN-BEREICH.md § "Mandanten-Detail, 8 Tabs" Punkt 7) hinzu — siehe
 * `AuditLogTabelle` unten.
 *
 * Filter-Stil identisch zu `pages/mail-protokoll.tsx`: jede Eingabe baut den `useApi`-Pfad direkt
 * neu (kein separater "Anwenden"-Knopf, kein Debounce — gleiches, bereits etabliertes Muster).
 *
 * Paginierung: `nextCursor` kommt über `useApi`s `zusatz` (additive Nebenfelder der Antwort, siehe
 * `hooks/useApi.ts`). "Mehr laden" hängt `cursor` an den Pfad an; die Seite sammelt die Zeilen
 * selbst in lokalem State auf, weil `useApi` je Pfad nur die JEWEILS letzte Antwort hält (kein
 * eigener Cache/keine Akkumulation). Ein Filterwechsel setzt `cursor` zurück UND ersetzt (statt
 * ergänzt) die gesammelten Zeilen — beides hängt am selben `cursor`-Wert, der zum Zeitpunkt der
 * Antwort aktuell war (React committet Zustandsänderungen aus einem Event-Handler gebündelt, bevor
 * der Effekt unten läuft — der Handler, der den Filter ändert, setzt `cursor` im selben Zug auf
 * `undefined`).
 */
import { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { formatDateTime } from "../../ui/format";
import { DateInput, Drawer, EmptyState, ErrorNote, LoadingRows, PageHeader } from "../../ui/components";

export interface AuditLogRow {
  id: string;
  adminId: string;
  action: string;
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  detail: unknown;
  createdAt: string;
}

/** Kurzfassung fuer die Tabellenzeile — der volle Payload steht ohnehin im Drawer. */
function kurzfassung(detail: unknown): string {
  if (detail == null) return "–";
  if (typeof detail === "string") return detail || "–";
  try {
    const json = JSON.stringify(detail);
    if (!json || json === "{}") return "–";
    return json.length > 90 ? `${json.slice(0, 90)}…` : json;
  } catch {
    return "–";
  }
}

/**
 * Tabellen-/Filter-/Drawer-Logik, herausgeloest aus der frueheren `AuditLogSeite`, damit sie
 * ZWEI Aufrufer bedienen kann (Admin-Bereich V2 P1, docs/KONZEPT-ADMIN-BEREICH.md §
 * "Mandanten-Detail, 8 Tabs" Punkt 7: "`platform_admin_audit_log` gefiltert auf diesen Mandanten,
 * Payload im Drawer" — Konzept nennt ausdruecklich "Wiederverwendung der Audit-Seiten-Logik ist
 * erwuenscht", keine zweite Kopie):
 *  1. `AuditLogSeite` (`/audit`, plattformweit, kein `tenantId`) — unveraendertes Verhalten.
 *  2. Der "Audit"-Tab in `tenant-detail.tsx` (`tenantId` gesetzt) — derselbe Code, zusaetzlich
 *     `?tenantId=` im Request (Server-Filter aus `auditLogQuerySchema`, Migration 372, bereits
 *     vor dieser Aenderung vorhanden) UND ohne die "Mandant"-Spalte (immer derselbe Wert bei
 *     gesetztem `tenantId` — redundant, keine neue Information).
 */
export function AuditLogTabelle({ tenantId }: { tenantId?: string }) {
  const [action, setAction] = useState("");
  const [von, setVon] = useState(""); // yyyy-mm-dd, aus DateInput (ISO)
  const [bis, setBis] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [zeilen, setZeilen] = useState<AuditLogRow[]>([]);
  const [ausgewaehlt, setAusgewaehlt] = useState<AuditLogRow | null>(null);

  /** Setzt einen Filter UND die Pagination im selben Zug zurueck — ein "Mehr laden" unter dem
   *  ALTEN Filter waere unter dem neuen sinnlos (anderer Zeilensatz). */
  function filterAendern(setter: (v: string) => void, wert: string) {
    setter(wert);
    setCursor(undefined);
  }

  const params = new URLSearchParams();
  if (action.trim()) params.set("action", action.trim());
  if (von) params.set("von", `${von}T00:00:00.000Z`);
  if (bis) params.set("bis", `${bis}T23:59:59.999Z`);
  if (tenantId) params.set("tenantId", tenantId);
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  const path = `/api/admin/audit-log${qs ? `?${qs}` : ""}`;

  const { data, zusatz, loading, error, reload } = useApi<AuditLogRow[], { nextCursor?: string }>(path);

  useEffect(() => {
    if (data == null) return;
    // Kein Cursor -> frischer Filter/Erstladung: ersetzen. Cursor gesetzt -> "Mehr laden": anhaengen.
    setZeilen((vorherige) => (cursor ? [...vorherige, ...data] : data));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- absichtlich nur `data`: `cursor` wird
    // zum Zeitpunkt dieses Effekt-Laufs bereits korrekt aktualisiert gelesen (siehe Kopfkommentar).
  }, [data]);

  const nextCursor = zusatz?.nextCursor;
  const gefiltert = Boolean(action.trim() || von || bis);
  const zeigtLeer = !loading && !error && zeilen.length === 0;

  return (
    <>
      <div className="card mb-4 flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          className="input !w-auto min-w-[220px] flex-1"
          placeholder="Aktion (z. B. tenant_update)"
          aria-label="Nach Aktion filtern"
          value={action}
          onChange={(e) => filterAendern(setAction, e.target.value)}
        />
        <label className="flex items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Von
          <DateInput
            className="!w-auto"
            inputClassName="!py-1.5"
            aria-label="Zeitraum von"
            value={von}
            onValueChange={(v) => filterAendern(setVon, v)}
          />
        </label>
        <label className="flex items-center gap-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Bis
          <DateInput
            className="!w-auto"
            inputClassName="!py-1.5"
            aria-label="Zeitraum bis"
            value={bis}
            onValueChange={(v) => filterAendern(setBis, v)}
          />
        </label>
      </div>

      {error && <ErrorNote message={error} onRetry={reload} />}

      {loading && zeilen.length === 0 ? (
        <div className="card">
          <LoadingRows rows={6} />
        </div>
      ) : zeigtLeer ? (
        <div className="card">
          <EmptyState
            icon="fact_check"
            title="Keine Einträge gefunden"
            hint={gefiltert ? "Kein Admin-Ereignis entspricht dieser Filterkombination." : "Es liegen noch keine Audit-Log-Einträge vor."}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className={`handy-karten w-full ${tenantId ? "min-w-[620px]" : "min-w-[760px]"} border-collapse text-left`}>
              <thead>
                <tr>
                  <th className="table-header">Zeit</th>
                  <th className="table-header">Admin</th>
                  <th className="table-header">Aktion</th>
                  {!tenantId && <th className="table-header">Mandant</th>}
                  <th className="table-header">Kurzfassung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {zeilen.map((zeile) => (
                  <tr
                    key={zeile.id}
                    className="cursor-pointer transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20"
                    onClick={() => setAusgewaehlt(zeile)}
                  >
                    <td className="table-cell whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {formatDateTime(zeile.createdAt)}
                    </td>
                    <td className="table-cell font-mono text-body-sm text-on-background dark:text-inverse-on-surface">{zeile.adminId}</td>
                    <td className="table-cell font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                      {zeile.action}
                    </td>
                    {!tenantId && (
                      <td className="table-cell font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                        {zeile.tenantName ?? "–"}
                      </td>
                    )}
                    <td className="table-cell max-w-[280px] truncate font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {kurzfassung(zeile.detail)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div className="flex justify-center border-t border-outline-variant/30 p-3 dark:border-outline/30">
              <button type="button" className="btn-secondary" disabled={loading} onClick={() => setCursor(nextCursor)}>
                {loading ? "Lädt …" : "Mehr laden"}
              </button>
            </div>
          )}
        </div>
      )}

      <Drawer open={ausgewaehlt != null} onClose={() => setAusgewaehlt(null)} title="Audit-Eintrag" subtitle={ausgewaehlt?.action}>
        {ausgewaehlt && (
          <div className="space-y-4">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 font-body-sm text-body-sm">
              <dt className="text-secondary dark:text-secondary-fixed-dim">Zeit</dt>
              <dd className="text-on-background dark:text-inverse-on-surface">{formatDateTime(ausgewaehlt.createdAt)}</dd>
              <dt className="text-secondary dark:text-secondary-fixed-dim">Admin</dt>
              <dd className="font-mono text-on-background dark:text-inverse-on-surface">{ausgewaehlt.adminId}</dd>
              {!tenantId && (
                <>
                  <dt className="text-secondary dark:text-secondary-fixed-dim">Mandant</dt>
                  <dd className="text-on-background dark:text-inverse-on-surface">
                    {ausgewaehlt.tenantName ? `${ausgewaehlt.tenantName} (${ausgewaehlt.tenantSlug})` : "–"}
                  </dd>
                </>
              )}
            </dl>
            <div>
              <p className="mb-1.5 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Payload
              </p>
              <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg bg-surface-container-low p-3 font-mono text-[12px] leading-relaxed text-on-background dark:bg-surface-variant/10 dark:text-inverse-on-surface">
                {JSON.stringify(ausgewaehlt.detail, null, 2) ?? "null"}
              </pre>
            </div>
          </div>
        )}
      </Drawer>
    </>
  );
}

export function AuditLogSeite() {
  return (
    <>
      <PageHeader title="Audit-Log" subtitle="Plattformweite Admin-Aktionen aus platform_admin_audit_log, filterbar." />
      <AuditLogTabelle />
    </>
  );
}
